import type { EnotaKazalnika, GibanjeKazalnika, KazalnikPregleda, SignalPregleda, VezavaKazalnika } from '@koda/shared';
import { useState, type JSX } from 'react';

import { decimalno } from '../lib/format';
import { useBesedila, type Besedila } from '../lib/jezik';
import { Lupa, sPredznakom } from './kazalniki-skupno';

/** Število v enoti kazalnika brez enote (enota je ob številki manjša). */
export function steviloKazalnika(v: string, enota: EnotaKazalnika, decimalke?: number): string {
  const privzeto = enota === 'odstotek' ? 1 : enota === 'eur' ? 0 : 2;
  return decimalno(v, decimalke ?? privzeto);
}

/** Enota ob številki: %, ×, €, »meseca«. */
export function enotaKazalnika(enota: EnotaKazalnika, b: Besedila): string {
  return enota === 'meseci' ? b.pk.enotaMeseci : enota === 'odstotek' ? '%' : enota === 'krat' ? '×' : '€';
}

/**
 * Lanska vrednost v eni vrstici: »lani 7,6 %«, pri neto dolgu »31. 12. 2025:
 * 1,16×«. Oznaka projekcije velja za letošnjo vrednost, zato je pod veliko
 * številko (`OznakaProjekcije`), ne tu.
 */
export function laniKazalnika(k: KazalnikPregleda, b: Besedila): string {
  const enota = enotaKazalnika(k.enota, b);
  if (k.lani === null) return `${k.nazivLani ?? b.pk.laniMalo} ${b.pk.ni}`;
  const st = steviloKazalnika(k.lani, k.enota, k.decimalke);
  if (k.nazivLani !== undefined) {
    const vrednost = k.enota === 'krat' ? `${st}${enota}` : `${st} ${enota}`;
    return `${k.nazivLani}: ${vrednost}`;
  }
  return `${b.pk.laniMalo} ${st} ${enota}`;
}

/** »projekcija na leto« pod letošnjo vrednostjo, kadar je projekcija; podrobnost v namigu. */
export function OznakaProjekcije({ kazalnik: k, razred }: { kazalnik: KazalnikPregleda; razred: string }): JSX.Element | null {
  const b = useBesedila();
  if (k.vrednost === null || k.oznaka === null) return null;
  return (
    <span className={razred} title={k.oznaka}>
      {b.pk.projekcijaNaLeto}
    </span>
  );
}

/**
 * Pomožna podatka ob mesecih (terjatve, zaloge, dobavitelji; 6.3): delež
 * letno preračunanega prometa in ocenjeni dnevi vezave. Samo podatek, brez
 * razčlenitve; kako so dnevi izračunani, pove namig, manjkajoč podatek je
 * »—« z razlogom v namigu.
 */
export function VezavaKartice({ vezava: v, razred }: { vezava: VezavaKazalnika; razred: string }): JSX.Element {
  const b = useBesedila();
  return (
    <div className={razred}>
      <div>
        <strong>{v.delez === null ? b.pk.ni : `${decimalno(v.delez, 1)} %`}</strong> <span className="kz-tiho">{v.nazivDeleza}</span>
      </div>
      <div title={v.razlogDni ?? v.opisDni}>
        <strong>{v.dnevi === null ? b.pk.ni : decimalno(v.dnevi, 0)}</strong> <span className="kz-tiho">{b.pk.dniVezave}</span>
        {v.dnevi !== null && v.dneviLani !== null && <span className="kz-tiho"> · {b.pk.dniVezaveLani(decimalno(v.dneviLani, 0))}</span>}
      </div>
    </div>
  );
}

/** Oznaka »Za pregled«: nevtralna, z ikono lupe (brez barve resnosti). */
export function OznakaZaPregled(): JSX.Element {
  const b = useBesedila();
  return (
    <span className="oznaka-za-pregled">
      <Lupa />
      {b.pk.zaPregled}
    </span>
  );
}

interface LastnostiKartice {
  kazalnik: KazalnikPregleda;
  signali: SignalPregleda[];
  odprt: boolean;
  naOdpri: () => void;
}

/**
 * Kartica kazalnika (točka 5): naziv in oznaka »Za pregled«, velika številka
 * z enoto, lanska vrednost, formula v eni vrstici, »Kaj pomeni?« in »Poglej
 * izračun«. Zneskov sestavin na kartici ni – so v izračunu.
 */
export function KarticaKazalnika({ kazalnik: k, signali, odprt, naOdpri }: LastnostiKartice): JSX.Element {
  const b = useBesedila();
  const [pomen, setPomen] = useState(false);
  return (
    <article id={`kazalnik-${k.koda}`} className={`kz-karta kz-kartica${signali.length > 0 ? ' s-signalom' : ''}${odprt ? ' odprta' : ''}`}>
      <div className="kz-kartica-glava">
        <h3>{k.naziv}</h3>
        {signali.length > 0 && <OznakaZaPregled />}
      </div>
      <div className="kz-kartica-vrednost-vrsta">
        <div className="kz-kartica-vrednost">
          {k.vrednost === null ? (
            <span className="kz-velika" title={k.razlog ?? undefined}>
              {b.pk.ni}
            </span>
          ) : (
            <>
              <span className="kz-velika">{steviloKazalnika(k.vrednost, k.enota, k.decimalke)}</span>
              <span className="kz-enota">{enotaKazalnika(k.enota, b)}</span>
            </>
          )}
        </div>
        {k.vezava !== undefined && <VezavaKartice vezava={k.vezava} razred="kz-vezava" />}
      </div>
      <OznakaProjekcije kazalnik={k} razred="kz-kartica-projekcija" />
      {k.vrednost === null && k.razlog !== null && <div className="kz-kartica-razlog">{k.razlog}</div>}
      <div className="kz-kartica-lani" title={k.lani === null ? (k.razlogLani ?? undefined) : undefined}>
        {laniKazalnika(k, b)}
      </div>
      {/* Dolga formula (npr. po meri) ostane v eni vrstici; cela je v namigu. */}
      <div className="kz-kartica-formula" title={k.formulaKratka ?? k.formula}>
        {k.formulaKratka ?? k.formula}
      </div>
      <div className="kz-povezave">
        <button
          type="button"
          className="kz-povezava"
          aria-expanded={pomen}
          onClick={() => {
            setPomen(!pomen);
          }}
        >
          {b.pk.kajPomeni}
        </button>
        <button type="button" className="kz-povezava" aria-expanded={odprt} onClick={naOdpri}>
          {odprt ? b.pk.skrijIzracun : b.pk.pogledIzracun}
        </button>
      </div>
      {pomen && <p className="kz-razlaga">{k.opis}</p>}
    </article>
  );
}

/** Rast z izrecnim predznakom v % ali »—«. */
function rastIzpis(rast: string | null, b: Besedila): string {
  return rast === null ? b.pk.ni : `${sPredznakom(rast)} %`;
}

/**
 * Okence »Gibanje« (točka 6): velika številka je razlika med rastjo
 * povprečnega stanja in rastjo prometa v o. t., pod njo obe rasti, nato stanje
 * na zadnji dan proti istemu dnevu lani, manjše od začetka leta in lani, ter
 * »Kaj pomeni?« in svoj »Poglej izračun« (izračun gibanja pod vrsto kartic).
 * Zneskov stanj v okencu ni (so v izračunu). Manjkajoč podatek je »—« z
 * razlogom v namigu.
 */
export function GibanjeOkence({
  naziv,
  gibanje: g,
  odprt,
  naOdpri,
}: {
  naziv: string;
  gibanje: GibanjeKazalnika;
  odprt: boolean;
  naOdpri: () => void;
}): JSX.Element {
  const b = useBesedila();
  const [pomen, setPomen] = useState(false);
  const p = g.povprecje;
  const st = g.stanje;
  const naslov = g.kratko?.naslov ?? `${b.pk.gibanje}: ${naziv}`;
  const ot = (r: string): string => `${sPredznakom(r)} ${b.pk.odstotneTocke}`;
  return (
    <section className={`kz-karta kz-gibanje${odprt ? ' odprta' : ''}`} aria-label={naslov}>
      <h3>{naslov}</h3>
      <div className="kz-gibanje-glavno">
        <div className="kz-kartica-vrednost">
          <span className="kz-velika kz-srednja" title={p.razlika === null ? (p.razlog ?? g.razlogPrometa ?? undefined) : undefined}>
            {p.razlika === null ? b.pk.ni : sPredznakom(p.razlika)}
          </span>
          {p.razlika !== null && <span className="kz-enota">{b.pk.odstotneTocke}</span>}
        </div>
        <span className="kz-tiho">
          {b.pk.gibanjePovprecnoStanje} {rastIzpis(p.rast, b)} · {g.kratko?.promet ?? g.nazivPrometa} {rastIzpis(g.rastPrometa, b)}
        </span>
      </div>
      <div className="kz-gibanje-stanje">
        <span>{g.kratko?.stanje ?? st.naziv}</span>
        <span className="kz-gibanje-vrednost" title={st.rast === null ? (st.razlog ?? undefined) : undefined}>
          <strong>{rastIzpis(st.rast, b)}</strong>
          {/* S čim se primerja razlika: »proti prihodkom« oz. »proti kreditnemu prometu«. */}
          {st.razlika !== null && (
            <span className="kz-tiho">
              {' '}
              ({ot(st.razlika)}
              {g.kratko?.proti === undefined ? '' : ` ${g.kratko.proti}`})
            </span>
          )}
        </span>
      </div>
      <div className="kz-gibanje-male">
        {[
          { naziv: g.kratko?.odZacetka ?? g.odZacetka.naziv, v: g.odZacetka },
          { naziv: g.lani.naziv, v: g.lani },
        ].map(({ naziv: n, v }) => (
          <div key={n}>
            <span>{n}</span>
            <span title={v.rast === null ? (v.razlog ?? undefined) : undefined}>{rastIzpis(v.rast, b)}</span>
          </div>
        ))}
      </div>
      <div className="kz-povezave">
        <button
          type="button"
          className="kz-povezava"
          aria-expanded={pomen}
          onClick={() => {
            setPomen(!pomen);
          }}
        >
          {b.pk.kajPomeni}
        </button>
        <button type="button" className="kz-povezava" aria-expanded={odprt} onClick={naOdpri}>
          {odprt ? b.pk.skrijIzracun : b.pk.pogledIzracun}
        </button>
      </div>
      {pomen && (
        <div className="kz-razlaga">
          {b.pk.gibanjePomen(g).map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
      )}
    </section>
  );
}

interface LastnostiSeznama {
  kazalniki: KazalnikPregleda[];
  signali: SignalPregleda[];
  odprt: string | null;
  naOdpri: (koda: string) => void;
}

/**
 * Kazalniki na telefonu (zasnova »Pregled – telefon«): seznam vrstic – naziv,
 * pod njim lani (in gibanje v o. t.), desno vrednost. Vrstica odpre izračun.
 */
export function SeznamKazalnikov({ kazalniki, signali, odprt, naOdpri }: LastnostiSeznama): JSX.Element {
  const b = useBesedila();
  return (
    <ul className="kz-seznam-kazalnikov">
      {kazalniki.map((k) => {
        const gib = k.gibanje?.povprecje.razlika;
        const vrednost =
          k.vrednost === null
            ? b.pk.ni
            : k.enota === 'krat' || k.enota === 'odstotek'
              ? `${steviloKazalnika(k.vrednost, k.enota, k.decimalke)}${enotaKazalnika(k.enota, b)}`
              : `${steviloKazalnika(k.vrednost, k.enota, k.decimalke)} ${enotaKazalnika(k.enota, b)}`;
        return (
          <li key={k.koda} id={`kazalnik-${k.koda}`}>
            <button
              type="button"
              className="kz-seznam-vrstica"
              aria-expanded={odprt === k.koda}
              onClick={() => {
                naOdpri(k.koda);
              }}
            >
              <span className="kz-seznam-ime">
                <span className="kz-seznam-naziv">
                  {k.naziv}
                  {signali.some((s) => s.kazalnik === k.koda) && (
                    <span className="kz-seznam-lupa" aria-label={b.pk.zaPregled}>
                      <Lupa />
                    </span>
                  )}
                </span>
                <span className="kz-tiho">
                  {laniKazalnika(k, b)}
                  {gib !== undefined && gib !== null && ` · ${b.pk.gibanje.toLocaleLowerCase()} ${sPredznakom(gib)} ${b.pk.odstotneTocke}`}
                </span>
              </span>
              <span className="kz-seznam-vrednost">
                {vrednost}
                <OznakaProjekcije kazalnik={k} razred="kz-seznam-projekcija" />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
